import { z } from 'zod';

// Zod schema for strong validation
const salarySchema = z.object({
  min: z.number(),
  max: z.number(),
  waluta: z.string(),
});

const careerPathSchema = z.object({
  nazwa: z.string(),
  opis: z.string(),
  zarobki: salarySchema,
  potencjalRozwoju: z.string(),
  korzysci: z.array(z.string()),
  wymagania: z.array(z.string()),
  umiejetnosci: z.array(z.string()).optional(),
});

const skillSchema = z.object({
  nazwa: z.string(),
  priorytet: z.number(),
  opis: z.string(),
});

const stepSchema = z.object({
  nazwa: z.string(),
  czasWykonania: z.string(),
  opis: z.string(),
});

const planRozwojuSchema = z.object({
  kroki: z.array(stepSchema),
  certyfikacje: z.array(z.string()),
  projekty: z.array(z.string()),
});

// Definicja typów dla analizy kariery

export interface Predyspozycja {
  nazwa: string;
  uzasadnienie: string;
  mocneStrony?: string[];
  wskazowki?: string[];
}

export interface Zarobki {
  min: number;
  max: number;
  waluta: string;
  okres?: string;
}

export interface SciezkaKariery {
  nazwa: string;
  opis: string;
  zarobki: Zarobki;
  umiejetnosci: string[];
  wymagania?: string[];
  perspektywy?: string;
  potencjalRozwoju?: string;
  korzysci?: string[];
}

export interface Umiejetnosc {
  nazwa: string;
  opis: string;
  priorytet: number;
  czasNauki?: string;
}

export interface CareerAnalysisData {
  predyspozycje: Predyspozycja[];
  sciezkiKariery: SciezkaKariery[];
  umiejetnosci: Umiejetnosc[];
  mocneStrony?: string[];
  obszaryRozwoju?: string[];
  timestamp?: number;
  planRozwoju?: {
    kroki: {
      nazwa: string;
      czasWykonania: string;
      opis: string;
    }[];
    certyfikacje: string[];
    projekty: string[];
  };
}

// Rozszerzony typ dla Zod schema
export type ZodCareerAnalysisData = z.infer<typeof careerAnalysisSchema>;

const careerAnalysisSchema = z.object({
  predyspozycje: z.array(z.object({
    nazwa: z.string(),
    uzasadnienie: z.string(),
  })),
  sciezkiKariery: z.array(careerPathSchema),
  umiejetnosci: z.array(skillSchema),
  planRozwoju: planRozwojuSchema.optional(),
});

/**
 * Bezpieczne parsowanie JSON lub string do obiektu analizy
 */
export function safeParseCareerAnalysis(data: string | any): CareerAnalysisData | null {
  try {
    // Jeśli dane są już obiektem, zwróć je
    if (typeof data === 'object' && data !== null) {
      return validateCareerAnalysis(data) ? data : null;
    }
    
    // Jeśli data jest stringiem, spróbuj sparsować jako JSON
    if (typeof data === 'string') {
      console.log('Próba parsowania analizy z JSON string');
      
      // Sprawdź czy string nie jest 'undefined' lub 'null'
      if (data === 'undefined' || data === 'null') {
        console.error('String analizy ma wartość "undefined" lub "null"');
        return null;
      }
      
      // Próba parsowania JSON
      const parsed = JSON.parse(data);
      return validateCareerAnalysis(parsed) ? parsed : null;
    }
    
    console.error('Dane analizy mają nieprawidłowy format', typeof data);
    return null;
  } catch (error) {
    console.error('Błąd parsowania danych analizy:', error);
    return null;
  }
}

/**
 * Sprawdza, czy obiekt analizy zawiera wszystkie wymagane pola
 */
export function validateCareerAnalysis(analysis: any): analysis is CareerAnalysisData {
  if (!analysis || typeof analysis !== 'object') {
    console.error('Analiza nie jest obiektem');
    return false;
  }

  // Sprawdź czy obiekt ma wszystkie wymagane pola
  const requiredFields = ['predyspozycje', 'sciezkiKariery', 'umiejetnosci', 'planRozwoju'];
  for (const field of requiredFields) {
    if (!analysis[field]) {
      console.error(`Brak wymaganego pola: ${field}`);
      return false;
    }
    
    // Sprawdź czy pole jest tablicą (jeśli powinno być)
    if (['predyspozycje', 'sciezkiKariery', 'umiejetnosci'].includes(field) && !Array.isArray(analysis[field])) {
      console.error(`Pole ${field} powinno być tablicą`);
      return false;
    }
  }

  // Wszystkie walidacje przeszły
  return true;
}

/**
 * Formatuje analizę do przechowywania (np. jako JSON string)
 */
export function formatAnalysisForStorage(analysis: CareerAnalysisData): string {
  try {
    return JSON.stringify(analysis);
  } catch (error) {
    console.error('Błąd podczas formatowania analizy do przechowywania:', error);
    throw error;
  }
}

// Funkcja generująca zastępcze dane, gdy parsowanie całkowicie zawiedzie
export function generateFallbackData(input: string): CareerAnalysisData {
  console.warn("Generating fallback career analysis data");
  
  // Próbujemy wydobyć z tekstu jakieś sensowne słowa kluczowe
  const text = input.toLowerCase();
  const keywords = [];
  
  if (text.includes('programowa')) keywords.push('programowanie');
  if (text.includes('developer') || text.includes('dewelopowanie')) keywords.push('developer');
  if (text.includes('analiz')) keywords.push('analityka');
  if (text.includes('data')) keywords.push('dane');
  if (text.includes('przedsiębiorca') || text.includes('biznes')) keywords.push('przedsiębiorczość');
  if (text.includes('marketing')) keywords.push('marketing');
  if (text.includes('ai') || text.includes('sztuczn')) keywords.push('AI');
  if (text.includes('cyber')) keywords.push('cyberbezpieczeństwo');
  
  // Jeśli nie znaleźliśmy żadnych słów kluczowych, użyjmy domyślnych
  if (keywords.length === 0) {
    keywords.push('AI', 'programowanie', 'analityka danych');
  }
  
  return {
    predyspozycje: [
      { 
        nazwa: 'Innowacyjność', 
        uzasadnienie: 'Wykazujesz zainteresowanie nowymi technologiami i innowacjami.' 
      },
      { 
        nazwa: 'Adaptacyjność', 
        uzasadnienie: 'Potrafisz dostosować się do zmieniających się warunków i nowych wyzwań.' 
      }
    ],
    sciezkiKariery: [
      {
        nazwa: `Specjalista ${keywords[0] || 'nowych technologii'}`,
        opis: 'Ścieżka kariery wykorzystująca najnowsze technologie i trendy rynkowe.',
        zarobki: { min: 7000, max: 15000, waluta: 'PLN' },
        potencjalRozwoju: 'Wysoki',
        korzysci: ['Praca z nowoczesnymi technologiami', 'Elastyczność', 'Ciągły rozwój'],
        wymagania: ['Znajomość aktualnych trendów', 'Umiejętność adaptacji', 'Chęć ciągłego uczenia się'],
        umiejetnosci: ['Znajomość technologii', 'Komunikacja', 'Rozwiązywanie problemów']
      }
    ],
    umiejetnosci: [
      {
        nazwa: keywords[0] || 'Programowanie',
        priorytet: 1,
        opis: 'Podstawowa umiejętność niezbędna w tej ścieżce kariery.'
      },
      {
        nazwa: 'Rozwiązywanie problemów',
        priorytet: 2,
        opis: 'Zdolność do znajdowania skutecznych rozwiązań złożonych problemów.'
      }
    ],
    planRozwoju: {
      kroki: [
        {
          nazwa: 'Kurs online',
          czasWykonania: '3 miesiące',
          opis: `Ukończenie kursu z zakresu ${keywords[0] || 'nowych technologii'}.`
        }
      ],
      certyfikacje: ['Certyfikat specjalisty'],
      projekty: ['Stworzenie własnego portfolio']
    }
  };
}
